import * as React from "react";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../models/states/StoreState";
import {
    setCarrouselActiveVariableIndex,
    validationClear,
    validationSetResult,
    setDefaultFocusStatus,
} from "../../redux/actions";
import useStyles from "./CarrouselControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import {
    useValidation,
    useDiscreteVariadicValidator,
    isAnswerRequired,
} from "../../hooks/useValidation";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceListControl } from "./ChoiceListControl";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { QuestionType } from "../../models/enums/QuestionType";
import { isQuestionSoftPrompt } from "../../models/utilities/questionUtilities";
import lottieGif from "../../assets/images/lottie.gif";
import { MOBILE_WIDTH, SMALL_WIDTH } from "../../consts";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { CarrouselNavigation } from "./KeyboardNavigation/CarrouselNavigation";
import useRecall from "../../hooks/useRecall";

interface StatementProps {
    id: string;
    key: string;
    text: string;
    color: string;
    imageUrl: string;
    isMobileView: boolean;
    isActiveItem: boolean;
    statementWidth: number;
    handleSwipe: (direction: string) => void;
    isShowLottieGesture: boolean;
}

const MOBILE_DIMENSIONS = 270;
const RESIZE_CAROUSEL = "resize-carousel";
const MOBILE_VIEW_GAP = 16;
const DESKTOP_VIEW_GAP = 64;
const DESKTOP_STATEMENT_WIDTH = 376;
const OFFSET_MARGIN_FOR_MOBILE = 20;
const PARTIAL_WIDTH_FOR_MOBILE = 20; // for prev and next statement

const Statement = React.forwardRef((props: StatementProps, ref) => {
    const {
        id,
        color,
        text,
        imageUrl,
        isMobileView,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
    } = props;
    const recalledText = useRecall(text);
    const recalledImage = useRecall(imageUrl);
    const theme: any = useTheme();
    const { ContentResponsivenessOption: contentResponsivenessOption } = theme;
    const justifyContent = recalledImage ? "space-between" : "center";
    const imageWrapperRef = React.useRef(null);
    const classes = useStyles({
        theme,
        color,
        justifyContent,
        isMobileView,
        contentResponsivenessOption,
        isActiveItem,
        statementWidth,
        isShowLottieGesture,
        imageUrl: recalledImage,
    });
    const containerRef = React.useRef(null);

    // Combine refs for forwarding
    const combinedRef = (element) => {
        containerRef.current = element;
        if (ref) {
            // Only handle function refs, which are safer to use
            if (typeof ref === "function") {
                ref(element);
            }
            // Don't try to set the current property directly as it may be read-only
        }
    };

    const imageRef = React.useRef(null);
    const textContainerRef = React.useRef(null);
    const [forceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        const container = containerRef.current;
        const image = imageRef.current;
        const wrapperContainer = imageWrapperRef.current;
        if (image) {
            image.addEventListener("load", responsiveRender);
        } else {
            resizeFont();
        }

        return () => {
            image && image.removeEventListener("load", responsiveRender);
        };

        function responsiveRender() {
            resizeCarousel(image, container, wrapperContainer);
            resizeFont();
        }

        function resizeFont() {
            const fontSize = getFontSize(container);
            container.style.fontSize = `${fontSize}px`;
        }
    }, [recalledImage, isMobileView, contentResponsivenessOption]);
    const isStatementHasContent = recalledText.replace("<br>", "").trim().length > 0;
    const resizeCarousel = (image, container, wrapperContainer) => {
        if (!image) return;
        const originalWidth = image.naturalWidth;
        const isSmallImage = statementWidth > originalWidth;
        const imageWidth = statementWidth < originalWidth ? "100%" : `${originalWidth}px`;
        image.style.width = imageWidth;
        container.style.width = `${statementWidth}px`;
        if (isSmallImage) {
            wrapperContainer.style.paddingTop = "16px";
        }
        if (isSmallImage && !isStatementHasContent) {
            wrapperContainer.style.paddingBottom = "16px";
        }
    };

    const getFontSize = (container) => {
        const DEFAULT_FONT_SIZE = 16;
        const MINIMUM_FONT_SIZE = 12;
        const MAXIMUM_FONT_SIZE = 30;
        const containerWidth = container.offsetWidth;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        context.font = `${DEFAULT_FONT_SIZE}px sans-serif`;
        const textWidth = context.measureText(recalledText).width;

        let fontSize = Math.floor((containerWidth / textWidth) * DEFAULT_FONT_SIZE);
        fontSize = Math.max(fontSize, MINIMUM_FONT_SIZE);
        fontSize = Math.min(fontSize, MAXIMUM_FONT_SIZE);

        if (textWidth < containerWidth && fontSize < DEFAULT_FONT_SIZE) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        if (contentResponsivenessOption === RESIZE_CAROUSEL) {
            fontSize = DEFAULT_FONT_SIZE;
        }
        return fontSize;
    };

    const ClampedHTML = ({ htmlContent, elementId }) => {
        return (
            <span
                className={isActiveItem ? "active-statement" : ""}
                id={elementId}
                style={{
                    display: "block",
                    WebkitLineClamp: "unset",
                    WebkitBoxOrient: "unset",
                    overflow: "hidden",
                    textOverflow: "clip",
                    wordWrap: "break-word",
                    lineHeight: "1.2em",
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        );
    };

    const [touchStartX, setTouchStartX] = React.useState(null);
    const [touchEndX, setTouchEndX] = React.useState(null);
    const SWIPE_THRESHOLD = 20; // Minimum distance for a valid swipe

    React.useEffect(() => {
        const handleTouchStart = (e) => {
            setTouchStartX(e.touches[0].clientX);
            setTouchEndX(null); // Reset touch end
        };

        const handleTouchMove = (e) => {
            setTouchEndX(e.touches[0].clientX);
        };

        const handleTouchEnd = () => {
            if (touchStartX === null || touchEndX === null) return;

            const deltaX = touchStartX - touchEndX;
            if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
                if (deltaX > 0) {
                    props.handleSwipe("next"); // Swipe Left detected
                } else {
                    props.handleSwipe("prev"); // Swipe Right detected
                }
            }
            // Reset swipe values
            setTouchStartX(null);
            setTouchEndX(null);
        };

        const element = containerRef.current;
        if (element) {
            element.addEventListener("touchstart", handleTouchStart);
            element.addEventListener("touchmove", handleTouchMove);
            element.addEventListener("touchend", handleTouchEnd);
        }

        return () => {
            if (element) {
                element.removeEventListener("touchstart", handleTouchStart);
                element.removeEventListener("touchmove", handleTouchMove);
                element.removeEventListener("touchend", handleTouchEnd);
            }
        };
    }, [touchStartX, touchEndX]);

    return (
        <div
            className={classes.statement}
            id={id}
            ref={combinedRef}
            tabIndex={-1}
            role="tabpanel"
            aria-labelledby={`${id}_label`}
        >
            {recalledImage && (
                <span className="image-wrapper" ref={imageWrapperRef}>
                    <img
                        className={`image-element`}
                        src={recalledImage}
                        ref={imageRef}
                        alt={recalledImage}
                    />
                </span>
            )}
            {isStatementHasContent && (
                <div className="statementTextContent" aria-hidden="false" ref={textContainerRef}>
                    <ClampedHTML
                        elementId="statementTextContent"
                        htmlContent={recalledText.trim()}
                        key={forceUpdateKey}
                    />
                </div>
            )}
        </div>
    );
});

const WrapperWithNavigationButtons = ({
    classes,
    isPrevBtnActive,
    isNextBtnActive,
    handleNavigationButtonClick,
    backButtonText,
    nextButtonText,
    isNumberNavigation,
    carrouselActiveVariableIndex,
    sequencedVariables,
    mentions,
    prevArrowRef,
    prevArrowOnKeyDown,
    nextArrowRef,
    nextArrowOnKeyDown,
}) => {
    const isKeyboardNavigation = React.useRef(false);

    // Use refs to directly access DOM elements
    const prevArrowDomRef = React.useRef(null);
    const nextArrowDomRef = React.useRef(null);

    // Effect to ensure aria-hidden and tabindex stay synchronized
    React.useEffect(() => {
        // Update prev arrow tabindex to match active state
        if (prevArrowDomRef.current) {
            prevArrowDomRef.current.setAttribute("tabindex", isPrevBtnActive ? "0" : "-1");
        }

        // Update next arrow tabindex to match active state
        if (nextArrowDomRef.current) {
            nextArrowDomRef.current.setAttribute("tabindex", isNextBtnActive ? "0" : "-1");
        }
    }, [isPrevBtnActive, isNextBtnActive]);

    // Enhanced function to merge refs with proper preservation of the navigation ref
    const mergeRefs = (navigationRef, domRef, isActive) => (element) => {
        // Update the DOM ref
        domRef.current = element;

        // Only register with keyboard navigation if the button is active
        if (isActive) {
            // This is critical: Call the function ref from useKeyboardNavigation
            // to properly register this element in the keyboard navigation system
            if (typeof navigationRef === "function") {
                navigationRef(element);
            } else if (navigationRef && "current" in navigationRef) {
                // Support for object refs as well, though useKeyboardNavigation uses function refs
                navigationRef.current = element;
            }
        }
    };

    return (
        <div
            className={classes.navigationButtonsPanel}
            role="group"
            aria-label="Carousel navigation"
        >
            <div
                className="fixedWidth"
                role="button"
                ref={mergeRefs(prevArrowRef, prevArrowDomRef, isPrevBtnActive)}
                aria-disabled={!isPrevBtnActive}
                aria-hidden={!isPrevBtnActive}
                aria-label={backButtonText}
                onKeyDown={(event) => {
                    if (event.key !== " " && event.key !== "Enter") {
                        prevArrowOnKeyDown(event);
                    } else if (isPrevBtnActive) {
                        // Handle space/enter ourselves
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("prev");
                        event.preventDefault();
                    }
                }}
                onClick={() => handleNavigationButtonClick("prev")}
            >
                {isPrevBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-left"]}
                        fixedWidth
                        className={classes.navigationButton}
                    />
                )}
            </div>

            {isNumberNavigation ? (
                <CounterNavigation
                    carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                    totalLength={sequencedVariables.length}
                />
            ) : (
                <BulletNavigation
                    mentions={mentions}
                    carrouselActiveVariableIndex={carrouselActiveVariableIndex}
                    sequencedVariables={sequencedVariables}
                    classes={classes}
                />
            )}

            <div
                className="fixedWidth"
                role="button"
                ref={mergeRefs(nextArrowRef, nextArrowDomRef, isNextBtnActive)}
                aria-disabled={!isNextBtnActive}
                aria-hidden={!isNextBtnActive}
                aria-label={nextButtonText}
                onKeyDown={(event) => {
                    if (event.key !== " " && event.key !== "Enter") {
                        nextArrowOnKeyDown(event);
                    } else if (isNextBtnActive) {
                        // Handle space/enter ourselves
                        isKeyboardNavigation.current = true;
                        handleNavigationButtonClick("next");
                        event.preventDefault();
                    }
                }}
                onClick={() => handleNavigationButtonClick("next")}
            >
                {isNextBtnActive && (
                    <FontAwesomeIcon
                        icon={["fas", "chevron-right"]}
                        fixedWidth
                        className={classes.navigationButton}
                    />
                )}
            </div>
        </div>
    );
};

const BulletNavigation = ({
    mentions,
    sequencedVariables,
    carrouselActiveVariableIndex,
    classes,
}) => {
    const itemsToDisplay = 6;
    const showFirstEllipse =
        itemsToDisplay < sequencedVariables.length &&
        itemsToDisplay / 2 < carrouselActiveVariableIndex + 1;
    const showLastEllipse =
        itemsToDisplay < sequencedVariables.length &&
        carrouselActiveVariableIndex < sequencedVariables.length - 4;
    const fixedTransformValue = 25;
    const [transformValue, setTransformValue] = React.useState(0);
    React.useEffect(() => {
        let newTransformValue = transformValue;
        if (sequencedVariables.length > 6) {
            if (
                carrouselActiveVariableIndex > 2 &&
                sequencedVariables.length - carrouselActiveVariableIndex > 3
            ) {
                newTransformValue = -fixedTransformValue * (carrouselActiveVariableIndex - 2);
            } else if (carrouselActiveVariableIndex < 3) {
                newTransformValue = 0;
            } else if (newTransformValue == 0 && carrouselActiveVariableIndex > itemsToDisplay) {
                newTransformValue =
                    -fixedTransformValue * (sequencedVariables.length - itemsToDisplay);
            }
            if (newTransformValue !== transformValue) {
                setTransformValue(newTransformValue); // Update state only if there's a change
            }
        }
    });
    return (
        <div className={classes.bulletNavigationPanel} role="group" aria-label="Item indicators">
            <div className="ellipse-item">{showFirstEllipse && <div></div>}</div>
            <div className="bullets-container">
                {sequencedVariables.map((variable, index) => {
                    const activeItem = carrouselActiveVariableIndex === index;
                    const answeredItem =
                        mentions.hasOwnProperty(variable.name) && mentions[variable.name].length;
                    const className =
                        activeItem && answeredItem
                            ? "active-answered-item"
                            : activeItem && !answeredItem
                                ? "active-not-answered-item"
                                : "";
                    return (
                        <div
                            key={index}
                            style={{
                                transform: `translateX(${transformValue}px)`,
                                transition: "transform 0.8s ease-in-out",
                            }}
                            className={className}
                            role="button"
                            tabIndex={-1}
                            aria-label={`Item ${index + 1} of ${sequencedVariables.length}${answeredItem ? ", answered" : ""
                                }`}
                            aria-current={activeItem ? "true" : "false"}
                        >
                            {activeItem && answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : answeredItem && !activeItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                >
                                    <rect
                                        x="0.5"
                                        y="0.5"
                                        width="17"
                                        height="17"
                                        rx="8.5"
                                        stroke="white"
                                    />
                                    <path
                                        d="M9 17C13.4187 17 17 13.4187 17 9C17 4.58125 13.4187 1 9 1C4.58125 1 1 4.58125 1 9C1 13.4187 4.58125 17 9 17ZM12.5312 7.53125L8.53125 11.5312C8.2375 11.825 7.7625 11.825 7.47188 11.5312L5.47188 9.53125C5.17813 9.2375 5.17813 8.7625 5.47188 8.47188C5.76562 8.18125 6.24062 8.17813 6.53125 8.47188L8 9.94063L11.4688 6.46875C11.7625 6.175 12.2375 6.175 12.5281 6.46875C12.8187 6.7625 12.8219 7.2375 12.5281 7.52812L12.5312 7.53125Z"
                                        fill="#316AEE"
                                    />
                                </svg>
                            ) : activeItem && !answeredItem ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        fill="#316AEE"
                                        stroke="white"
                                        strokeWidth="2"
                                    />
                                </svg>
                            ) : (
                                <div className="bullet" role="radio"></div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="ellipse-item">{showLastEllipse && <div></div>}</div>
        </div>
    );
};

const CounterNavigation = ({ carrouselActiveVariableIndex, totalLength }) => {
    return (
        <div
            className="counter-navigation"
            aria-label={`${carrouselActiveVariableIndex + 1}/${totalLength}`}
        >{`${carrouselActiveVariableIndex + 1}/${totalLength}`}</div>
    );
};

export function CarrouselControl(props: ControlProps) {
    const {
        question,
        sequencedVariables,
        validateSignal,
        mentions,
        questionnaire,
        isDesktopPreviewView,
        showSoftPrompt,
    } = props;
    const questionHasAtleastOneVariable = sequencedVariables && sequencedVariables.length > 0;

    // This condition only occurs if all the statements is eliminated then only question description is displayed
    if (!questionHasAtleastOneVariable) {
        return "";
    }

    const instanceId = `${question.id}_statement`;
    const isNumberNavigation = question.settings.NavigationStyle != "Bullets";
    let hideVariableBackArrow = false;
    let canLoopVariable = false;
    let advanceAutomatically = false;
    let choiceListAnswerType = QuestionType[QuestionType.CheckBox];
    let activeStatementMentionsCount = 0;
    const questionName = question.name;
    const dispatch = useDispatch();
    const isPrevBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isPrevBtnActive || false,
    );
    const isNextBtnActive = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.isNextBtnActive || false,
    );
    const carrouselActiveVariableIndex = useSelector(
        (state: StoreState) => state.interview.carrousel[instanceId]?.activeVariableIndex || 0,
    );

    // Create a variable object for keyboard navigation
    const AbovePreArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_above_pre`,
            name: "carrouselVarAbovePrev",
            id: `${instanceId}_var_above_pre`,
        }),
        [instanceId, question.id],
    );

    const AboveNextArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_above_next`,
            name: "carrouselVarAboveNext",
            id: `${instanceId}_var_above_next`,
        }),
        [instanceId, question.id],
    );

    const BelowPrevArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_below_pre`,
            name: "carrouselVarBelowPrev",
            id: `${instanceId}_var_below_pre`,
        }),
        [instanceId, question.id],
    );

    const BelowNextArrowVariable = React.useMemo(
        () => ({
            elementId: `${instanceId}_var_below_next`,
            name: "carrouselVarBelowNext",
            id: `${instanceId}_var_below_next`,
        }),
        [instanceId, question.id],
    );

    // Get active variable info (rename this version to avoid conflicts with the one below)
    const currentActiveCarouselVariable = sequencedVariables[carrouselActiveVariableIndex];
    const currentCarouselVariableName = currentActiveCarouselVariable?.name || "";

    // Add a ref to track previous active variable
    const prevActiveVariableRef = React.useRef(currentCarouselVariableName);

    // Setup keyboard navigation for prev/next arrows with active variable name in ID
    // Register prev arrow first to match natural left-to-right reading order
    const {
        elementReference: abovePrevArrowRef,
        onKeyDown: abovePrevArrowOnKeyDown,
    } = useKeyboardNavigation(
        `Above_CarrouselPrevArrow_${currentCarouselVariableName}`,
        AbovePreArrowVariable,
        question,
        CarrouselNavigation,
        () => handleNavigationButtonClick("prev"),
    );

    const {
        elementReference: aboveNextArrowRef,
        onKeyDown: aboveNextArrowOnKeyDown,
    } = useKeyboardNavigation(
        `Above_CarrouselNextArrow_${currentCarouselVariableName}`,
        AboveNextArrowVariable,
        question,
        CarrouselNavigation,
        () => handleNavigationButtonClick("next"),
    );

    const {
        elementReference: belowPrevArrowRef,
        onKeyDown: belowPrevArrowOnKeyDown,
    } = useKeyboardNavigation(
        `Below_CarrouselPrevArrow_${currentCarouselVariableName}`,
        BelowPrevArrowVariable,
        question,
        CarrouselNavigation,
        () => handleNavigationButtonClick("prev"),
    );

    const {
        elementReference: belowNextArrowRef,
        onKeyDown: belowNextArrowOnKeyDown,
    } = useKeyboardNavigation(
        `Below_CarrouselNextArrow_${currentCarouselVariableName}`,
        BelowNextArrowVariable,
        question,
        CarrouselNavigation,
        () => handleNavigationButtonClick("next"),
    );

    // Register statement refs for keyboard navigation
    const statementRefs = React.useRef([]);
    const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);

    // Initialize refs array for statements
    React.useEffect(() => {
        statementRefs.current = sequencedVariables.map(() => React.createRef());
    }, [sequencedVariables.length]);

    React.useEffect(() => {
        if (!defaultFocusStatus?.statuses?.length) return;

        const carouselElements = [];
        const carouselIndices = [];

        for (let i = 0; i < defaultFocusStatus.statuses.length; i++) {
            const status = defaultFocusStatus.statuses[i];
            const elementId = status.elementId?.toString() || "";

            if (elementId.includes(currentCarouselVariableName)) {
                carouselElements.push(status);
                carouselIndices.push(i);
            }
        }

        if (carouselElements.length > 1) {
            const firstCarouselIndex = Math.min(...carouselIndices);

            const aboveArrowElements = [];
            const belowArrowElements = [];
            const inputElements = [];

            for (const element of carouselElements) {
                const elementId = element.elementId?.toString() || "";
                if (
                    elementId.includes("Above_CarrouselPrevArrow") ||
                    elementId.includes("Above_CarrouselNextArrow")
                ) {
                    aboveArrowElements.push(element);
                } else if (
                    elementId.includes("Below_CarrouselPrevArrow") ||
                    elementId.includes("Below_CarrouselNextArrow")
                ) {
                    belowArrowElements.push(element);
                } else {
                    inputElements.push(element);
                }
            }

            const allArrowElements = [...aboveArrowElements, ...belowArrowElements];
            if (allArrowElements.length && inputElements.length) {
                const arrowIndices = allArrowElements.map((e) =>
                    defaultFocusStatus.statuses.indexOf(e),
                );
                const inputIndices = inputElements.map((e) =>
                    defaultFocusStatus.statuses.indexOf(e),
                );

                const minArrowIndex = Math.min(...arrowIndices);
                const minInputIndex = Math.min(...inputIndices);

                if (
                    minInputIndex < minArrowIndex ||
                    !arrowIndices.every((_, i, arr) => i === 0 || arr[i - 1] <= arr[i])
                ) {
                    // Remove all carousel elements
                    for (let i = defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                        if (carouselIndices.includes(i)) {
                            defaultFocusStatus.statuses.splice(i, 1);
                        }
                    }

                    // Reorder and reinsert: above → inputs → below
                    const reordered = [
                        ...aboveArrowElements,
                        ...inputElements,
                        ...belowArrowElements,
                    ];
                    defaultFocusStatus.statuses.splice(firstCarouselIndex, 0, ...reordered);

                    // Dispatch the update
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }
    }, [defaultFocusStatus, currentCarouselVariableName, dispatch]);

    // Simple effect to replace variable names while preserving positions
    React.useEffect(() => {
        if (!defaultFocusStatus || !defaultFocusStatus.statuses) return;

        const prevVariableName = prevActiveVariableRef.current;

        // Only process if previous variable exists and is different from current
        if (prevVariableName && prevVariableName !== currentCarouselVariableName) {
            let updated = false;

            // Find current variable ID from sequencedVariables
            const currentVarObj = sequencedVariables.find(
                (v) => v.name === currentCarouselVariableName,
            );
            const prevVarObj = sequencedVariables.find((v) => v.name === prevVariableName);

            if (currentVarObj?.id && prevVarObj?.id) {
                // Loop through all elements and replace variable names in place
                for (let i = 0; i < defaultFocusStatus.statuses.length; i++) {
                    const status = defaultFocusStatus.statuses[i];
                    const elementId = status.elementId ? status.elementId.toString() : "";

                    // Carousel arrows - check BOTH pattern AND variableId
                    const isCarouselArrow =
                        (status.variableId.toString() === AbovePreArrowVariable.id ||
                            status.variableId.toString() === AboveNextArrowVariable.id ||
                            status.variableId.toString() == BelowPrevArrowVariable.id ||
                            status.variableId.toString() == BelowNextArrowVariable.id) &&
                        (elementId.includes(`CarrouselPrevArrow_${prevVariableName}`) ||
                            elementId.includes(`CarrouselNextArrow_${prevVariableName}`));

                    // Carousel statements - check by instance ID pattern
                    const isCarouselStatement =
                        elementId.includes(`${instanceId}_statement`) &&
                        elementId.includes(prevVariableName);

                    // Choice elements - check by variable ID
                    const isChoiceElement =
                        status.variableId === prevVarObj.id &&
                        (elementId.includes("_C_") || elementId.includes("_INPUT"));

                    // Only update elements that match our criteria
                    if (isCarouselArrow || isCarouselStatement || isChoiceElement) {
                        // Replace the variable name in the element ID
                        defaultFocusStatus.statuses[i].elementId = elementId.replace(
                            prevVariableName,
                            currentCarouselVariableName,
                        );

                        // Update variableId for choice elements only
                        if (isChoiceElement) {
                            defaultFocusStatus.statuses[i].variableId = currentVarObj.id;
                        }

                        updated = true;
                    }
                }

                // Update Redux store only if changes were made
                if (updated) {
                    dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
                }
            }
        }

        // Update ref for next comparison
        prevActiveVariableRef.current = currentCarouselVariableName;
    }, [
        currentCarouselVariableName,
        defaultFocusStatus,
        dispatch,
        sequencedVariables,
        AbovePreArrowVariable.id,
        AboveNextArrowVariable.id,
        BelowPrevArrowVariable.id,
        BelowNextArrowVariable.id,
        instanceId,
    ]);

    // Update the dependency array to include carrouselActiveVariableIndex
    React.useEffect(() => {
        if (!defaultFocusStatus || !defaultFocusStatus.statuses) return;

        // Run this after component is fully mounted and choice elements are registered
        // This will reorder the focus array to ensure carousel elements come before choice elements
        const choiceElements = [];
        const activeChoiceElements = []; // For choice elements of the active statement
        const carouselElements = [];
        const otherElements = [];

        // Get the active variable name for special handling
        const activeVariableName = sequencedVariables[carrouselActiveVariableIndex]?.name;

        // First, categorize all elements
        defaultFocusStatus.statuses.forEach((status) => {
            // Make sure elementId is a string before using includes
            const elementId = status.elementId ? status.elementId.toString() : "";

            // Check if elementId contains any of the variable names from sequencedVariables
            const isChoiceElement = sequencedVariables.some(
                (variable) => variable.name && elementId.includes(variable.name),
            );

            // Special handling for active variable's choice elements
            if (isChoiceElement && activeVariableName && elementId.includes(activeVariableName)) {
                activeChoiceElements.push(status);
            }
            // Other choice elements
            else if (isChoiceElement) {
                choiceElements.push(status);
            }
            // Carousel elements have our custom variable ID
            else if (
                status.variableId.toString() === AbovePreArrowVariable.id ||
                status.variableId.toString() === AboveNextArrowVariable.id ||
                status.variableId.toString() == BelowPrevArrowVariable.id ||
                status.variableId.toString() == BelowNextArrowVariable.id
            ) {
                carouselElements.push(status);
            }
            // Any other elements
            else {
                otherElements.push(status);
            }
        });

        // Now reconstruct the array with the correct order:
        // 1. Other elements (come first)
        // 2. Carousel navigation elements (before choice elements)
        // 3. Active variable choice elements (come next for proper tab order)
        // 4. Other choice elements (come last)
        if (
            (choiceElements.length > 0 || activeChoiceElements.length > 0) &&
            carouselElements.length > 0
        ) {
            defaultFocusStatus.statuses = [
                ...otherElements,
                ...carouselElements,
                ...activeChoiceElements,
                ...choiceElements,
            ];

            // Update the focus status in Redux
            dispatch(setDefaultFocusStatus({ ...defaultFocusStatus }));
        }
        // Add carrouselActiveVariableIndex to dependency array so it runs when active statement changes
    }, [
        sequencedVariables,
        AbovePreArrowVariable.id,
        AboveNextArrowVariable.id,
        BelowPrevArrowVariable.id,
        BelowNextArrowVariable.id,
        defaultFocusStatus,
        dispatch,
        carrouselActiveVariableIndex,
    ]);

    if (hasSetting(question, "HideVariableBackArrow")) {
        hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
    }

    if (hasSetting(question, "LoopVariable")) {
        canLoopVariable = getSettingBoolean(question, "LoopVariable");
    }

    if (hasSetting(question, "AutoAdvanceVariable")) {
        advanceAutomatically = getSettingBoolean(question, "AutoAdvanceVariable");
    }

    if (hasSetting(question, "CarrouselChoiceListAnswerType")) {
        choiceListAnswerType = getSettingString(question, "CarrouselChoiceListAnswerType");
    }

    const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
    const [language] = useLanguage();
    const theme = useTheme();
    const counter = React.useRef(1);
    const containerRef = React.useRef<HTMLDivElement>();
    const [deviceWidth] = React.useState<number>(window.innerWidth);
    const [deviceHeight] = React.useState<number>(window.innerHeight);
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const isMobileView = !isDesktopPreviewView && (deviceWidth <= MOBILE_WIDTH || mobilePreview);
    const backButtonAriaLabelText = translate(questionnaire.theme, "BackButton", language);
    const nextButtonAriaLabelText = translate(questionnaire.theme, "NextButton", language);
    const isAboveChoiceNavigation = question.settings?.NavigationDisplay?.includes("Above") ?? true;
    const isBelowChoiceNavigation =
        question.settings?.NavigationDisplay?.includes("Below") ?? false;
    const justifyContent = isMobileView ? "space-between" : "center";
    const classes = useStyles({ theme, isMobileView, justifyContent, isAboveChoiceNavigation });
    const [statementValidation, setStatementValidationCheck] = React.useState({
        action: "NONE",
        direction: "NONE",
    });
    const [directionSaved, setDirection] = React.useState("NONE");
    const errorMonitor = useSelector(
        (state: StoreState) => state.interview.validation.errorsByVariableName,
    );
    let prevErrorMonitorRef = React.useRef(null);
    const [isCarouselVisible, setIsCarouselVisible] = React.useState(false);
    const [isFirstTimeRender, setIsFirstTimeRender] = React.useState(true);
    const [showLottieGesture, setShowLottieGesture] = React.useState(true);
    const isShowLottieGesture = showLottieGesture && isMobileView;

    React.useEffect(() => {
        const questionContainerHeight = containerRef.current?.offsetHeight;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isMobileView) {
                    setIsCarouselVisible(entry.isIntersecting); // Update visibility state
                }
            },
            {
                root: null, // Use the viewport as the root
                threshold: questionContainerHeight > deviceHeight ? 0.5 : 0.9, // Trigger based on viewport
            },
        );
        if (containerRef.current) {
            observer.observe(containerRef.current); // Start observing the carousel container
        }
        // Cleanup the observer on component unmount
        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        if (isCarouselVisible) {
            if (isFirstTimeRender) {
                setIsFirstTimeRender(false);
                if (showLottieGesture) {
                    setTimeout(() => {
                        setShowLottieGesture(false);
                    }, 2000);
                }
            }
        }
    }, [isCarouselVisible]);
    // This condition occurs if due to statement elimination some variable got removed and greater active index is previously stored
    if (!currentActiveCarouselVariable) {
        if (questionHasAtleastOneVariable && carrouselActiveVariableIndex !== 0) {
            // dispatch will be triggered with some delay then exception so setting active variable here
            dispatch(
                setCarrouselActiveVariableIndex(
                    instanceId,
                    0,
                    backButtonAlwaysVisible,
                    canLoopVariable || counter.current !== sequencedVariables.length,
                ),
            );
        } else {
            return "";
        }
    }

    const variableHasChoices =
        currentActiveCarouselVariable &&
        currentActiveCarouselVariable.choiceList &&
        currentActiveCarouselVariable.choiceList.length > 0;
    const variableName = currentActiveCarouselVariable?.name || "";
    const isSoftPrompt = isQuestionSoftPrompt(question);
    const isRequired = isAnswerRequired(question, questionnaire) && !isSoftPrompt;
    const defaultMinRequiredAnswerValue = 1;

    const getMinRequiredAnswers = React.useCallback(() => {
        const number = getSettingNumber(question, "MinRequiredAnswers");
        if (number !== undefined && !Number.isNaN(number)) {
            return number === 0 ? defaultMinRequiredAnswerValue : number;
        }
        return null;
    }, [question, defaultMinRequiredAnswerValue]);

    const minRequiredAnswers = getMinRequiredAnswers();

    if (
        mentions &&
        Object.keys(mentions).length > 0 &&
        mentions[variableName] &&
        mentions[variableName].length > 0
    ) {
        activeStatementMentionsCount = mentions[variableName].length;
    }
    const activeStatementHasRequiredMentions =
        activeStatementMentionsCount >= (minRequiredAnswers ?? defaultMinRequiredAnswerValue);

    React.useEffect(() => {
        if (isSoftPrompt) {
            if (JSON.stringify(prevErrorMonitorRef.current) === JSON.stringify(errorMonitor)) {
                return;
            }

            prevErrorMonitorRef.current = errorMonitor;

            if (
                !errorMonitor ||
                !question ||
                !sequencedVariables ||
                sequencedVariables.length === 0
            ) {
                return;
            }
            if (errorMonitor.hasOwnProperty(question.name)) {
                const errors = errorMonitor[question.name];
                if (Array.isArray(errors) && errors.length > 0) {
                    const lastError = errors[errors.length - 1];
                    const errorVariableIndex = sequencedVariables.findIndex(
                        (variable) => variable.name === lastError.varInfo,
                    );
                    counter.current = errorVariableIndex + 1;
                    const newPrevBtnActive = backButtonAlwaysVisible || errorVariableIndex > 0;
                    const newNextBtnActive =
                        canLoopVariable || errorVariableIndex < sequencedVariables.length - 1;
                    dispatch(
                        setCarrouselActiveVariableIndex(
                            instanceId,
                            errorVariableIndex,
                            newPrevBtnActive,
                            newNextBtnActive,
                        ),
                    );
                }
            }
        }
    }, [errorMonitor]);

    React.useEffect(() => {
        if (statementValidation.action === "VALIDATEMENTIONS") {
            if (activeStatementHasRequiredMentions) {
                scrollHandler(statementValidation.direction, sequencedVariables);
                setStatementValidationCheck({
                    action: "MENTIONSVALIDATEDSUCCESSFULLY",
                    direction: "NONE",
                });
                dispatch(validationClear(true, true, [questionName]));
            } else {
                setStatementValidationCheck({
                    action: "SHOWMENTIONERROR",
                    direction: "NONE",
                });

                const errorKey =
                    activeStatementMentionsCount === 0 ? "IncompleteAnswer" : "InvalidChoicesMin";

                const minChoices = minRequiredAnswers ?? defaultMinRequiredAnswerValue;

                setValidationError(errorKey, minChoices);
            }
        }
    }, [statementValidation]);

    React.useEffect(() => {
        const index = counter.current - 1;
        const canLoop = canLoopVariable || counter.current !== sequencedVariables.length;

        dispatch(
            setCarrouselActiveVariableIndex(instanceId, index, backButtonAlwaysVisible, canLoop),
        );
    }, []); // Runs once on component mount

    const scrollHandler = (direction, vars) => {
        const isPrev = direction === "prev";

        // eslint-disable-next-line no-nested-ternary
        counter.current = isPrev
            ? canLoopVariable && counter.current === 1
                ? vars.length
                : counter.current - 1
            : canLoopVariable && counter.current === vars.length
                ? 1
                : Math.min(counter.current + 1, sequencedVariables.length);

        const newPrevBtnActive = isPrev
            ? backButtonAlwaysVisible || counter.current > 1
            : !hideVariableBackArrow;

        const newNextBtnActive = canLoopVariable || counter.current !== sequencedVariables.length;
        if (directionSaved !== direction) {
            setDirection(direction);
        }
        dispatch(
            setCarrouselActiveVariableIndex(
                instanceId,
                counter.current - 1,
                newPrevBtnActive,
                newNextBtnActive,
            ),
        );
        dispatch(validationClear(true, true, [questionName]));
    };

    const handleNavigationButtonClick = React.useCallback(
        (direction, isAfterSelectionAction = false) => {
            // If no choices is there then no validation check on arrow click (Can be due to elimination process)
            if (variableHasChoices) {
                const notRequiredButMinChoiceIsDefinedValidation =
                    !isRequired &&
                    minRequiredAnswers &&
                    activeStatementMentionsCount !== 0 &&
                    !activeStatementHasRequiredMentions;

                // For automatically advance(radio only) and prev button click move to next statement
                if (isAfterSelectionAction || (direction === "prev" && !canLoopVariable)) {
                    scrollHandler(direction, sequencedVariables);
                } else if (isRequired || notRequiredButMinChoiceIsDefinedValidation) {
                    setStatementValidationCheck({
                        action: "VALIDATEMENTIONS",
                        direction,
                    });
                } else {
                    scrollHandler(direction, sequencedVariables);
                }
            } else {
                scrollHandler(direction, sequencedVariables);
            }
        },
        [
            isRequired,
            sequencedVariables,
            minRequiredAnswers,
            activeStatementMentionsCount,
            activeStatementHasRequiredMentions,
            variableHasChoices,
        ],
    );

    const onAfterCarouselChoiceSelection = React.useCallback(
        (isAfterSelectionAction) => {
            if (
                advanceAutomatically &&
                choiceListAnswerType === QuestionType[QuestionType.RadioButton]
            ) {
                if (sequencedVariables.length - 1 > carrouselActiveVariableIndex) {
                    setTimeout(() => {
                        handleNavigationButtonClick("next", isAfterSelectionAction);

                        if (containerRef.current?.offsetParent instanceof Element) {
                            const offsetParent = containerRef.current.offsetParent as HTMLElement;
                            offsetParent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 300);
                }
            }
        },
        [
            sequencedVariables,
            carrouselActiveVariableIndex,
            advanceAutomatically,
            choiceListAnswerType,
            handleNavigationButtonClick,
        ],
    );

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const commonProps = {
        isNextBtnActive,
        isPrevBtnActive,
        handleNavigationButtonClick,
        classes,
        carrouselActiveVariableIndex,
        isNumberNavigation,
        sequencedVariables,
        mentions,
    };

    const gap = isMobileView ? MOBILE_VIEW_GAP : DESKTOP_VIEW_GAP;
    const borderWidth = 1;
    const noOfSides = 2; // both side value i.e left and right
    const bordersTotalWidth = noOfSides * borderWidth;
    let statementWidth;
    if (isMobileView && deviceWidth < SMALL_WIDTH) {
        statementWidth =
            deviceWidth -
            (noOfSides * OFFSET_MARGIN_FOR_MOBILE +
                noOfSides * MOBILE_VIEW_GAP +
                noOfSides * PARTIAL_WIDTH_FOR_MOBILE);
    } else if (
        isDesktopPreviewView != null &&
        !isDesktopPreviewView &&
        deviceWidth > MOBILE_WIDTH
    ) {
        statementWidth = MOBILE_DIMENSIONS;
    } else {
        statementWidth = DESKTOP_STATEMENT_WIDTH;
    }
    const itemWidth = statementWidth + bordersTotalWidth;
    const TOTAL_ITEM_WIDTH =
        itemWidth * sequencedVariables.length - 1 + gap * (sequencedVariables.length - 3);
    const transformValue =
        TOTAL_ITEM_WIDTH / 2 -
        itemWidth / 2 +
        gap -
        (itemWidth + gap) * carrouselActiveVariableIndex;
    const handleSwipe = (direction) => {
        handleNavigationButtonClick(direction);
    };

    // Fix validation error model type
    const setValidationError = (errorKey: string, minChoices: number) => {
        const questionValidationError = {
            [questionName]: [
                {
                    key: errorKey,
                    softPrompt: showSoftPrompt,
                    args: { min: minChoices.toString() },
                },
            ],
        };

        dispatch(
            validationSetResult({
                errorsByVariableName: questionValidationError,
                isValid: false,
            }),
        );
    };

    return (
        <div className={classes.container} ref={containerRef} role="tablist">
            <div className={classes.statementsContainer}>
                {sequencedVariables.map((variable, index) => (
                    <div
                        key={variable.name}
                        style={{
                            transform: `translateX(${transformValue}px)`,
                            transition: "transform 0.8s ease-in-out",
                        }}
                    >
                        {index === 0 && isShowLottieGesture && isCarouselVisible && (
                            <img
                                src={lottieGif}
                                className="lottie-gesture"
                                alt="Swipe gesture hint"
                            />
                        )}
                        <Statement
                            id={variable.elementId}
                            key={variable.name}
                            color={getSettingString(variable, "Color", "#F2F2F2")}
                            text={translate(variable, "text", language)}
                            imageUrl={translate(variable, "imageUrl", language)}
                            isMobileView={isMobileView}
                            isActiveItem={carrouselActiveVariableIndex === index}
                            statementWidth={statementWidth}
                            handleSwipe={handleSwipe}
                            isShowLottieGesture={isShowLottieGesture}
                            ref={statementRefs.current[index]}
                        />
                    </div>
                ))}
            </div>
            {isAboveChoiceNavigation ? (
                <WrapperWithNavigationButtons
                    {...commonProps}
                    backButtonText={backButtonAriaLabelText}
                    nextButtonText={nextButtonAriaLabelText}
                    prevArrowRef={abovePrevArrowRef}
                    prevArrowOnKeyDown={abovePrevArrowOnKeyDown}
                    nextArrowRef={aboveNextArrowRef}
                    nextArrowOnKeyDown={aboveNextArrowOnKeyDown}
                />
            ) : (
                ""
            )}
            <div className={classes.choiceAndNavigationContainer}>
                <ControlContainer
                    controlType={ChoiceListControl}
                    onAfterSelection={() => {
                        onAfterCarouselChoiceSelection(true);
                    }}
                    isSingleOnPage={props.isSingleOnPage}
                    onNavigation={props.onNavigation}
                    {...getRendererProps(props)}
                />
                {isBelowChoiceNavigation ? (
                    <WrapperWithNavigationButtons
                        {...commonProps}
                        backButtonText={backButtonAriaLabelText}
                        nextButtonText={nextButtonAriaLabelText}
                        prevArrowRef={belowPrevArrowRef}
                        prevArrowOnKeyDown={belowPrevArrowOnKeyDown}
                        nextArrowRef={belowNextArrowRef}
                        nextArrowOnKeyDown={belowNextArrowOnKeyDown}
                    />
                ) : (
                    ""
                )}
            </div>
        </div>
    );
}
